import React, { useState, useEffect, useRef, useCallback } from 'react';
import { isEqual } from 'lodash';
import { useViewport } from '@fairygodboss/ui';
import { MobileFilter } from './MobileFilter';
import { DesktopRender } from './Filters/DesktopRender';

export const JobsSearchFilter = ({
    filterJobs,
    activeFilters,
    onClearFilters,
    setFilterByType,
    locationList,
    companyList,
    isAuthenticatedUser,
    hasRemote,
    companySizes,
}) => {
    const [activeCompanies, setActiveCompanies] = useState([]);
    const [companiesOpen, isCompaniesOpen] = useState(false);
    const [activeCompanySizes, setActiveCompanySizes] = useState([]);
    const [companySizeOpen, isCompanySizeOpen] = useState(false);
    const [activeLocations, setActiveLocations] = useState([]);
    const [locationsOpen, isLocationsOpen] = useState(false);
    const [remoteSelected, setRemoteSelected] = useState(
        !!activeFilters?.remote?.length
    );
    const companiesFilterRef = useRef();
    const companySizeFilterRef = useRef();
    const locationFiltersRef = useRef();

    const setLocationOpen = useCallback(
        (open) => {
            isLocationsOpen(open);
            if (open && companiesOpen) {
                companiesFilterRef?.current?.setFilter();
                isCompaniesOpen(false);
            }
            if (open && companySizeOpen) {
                companySizeFilterRef?.current?.setFilter();
                isCompanySizeOpen(false);
            }
        },
        [companiesOpen, companySizeOpen, isLocationsOpen]
    );

    const setCompaniesOpen = useCallback(
        (open) => {
            isCompaniesOpen(open);
            if (open && locationsOpen) {
                locationFiltersRef?.current?.setFilter();
                isLocationsOpen(false);
            }
            if (open && companySizeOpen) {
                companySizeFilterRef?.current?.setFilter();
                isCompanySizeOpen(false);
            }
        },
        [companySizeOpen, locationsOpen]
    );

    const setCompanySizeOpen = useCallback(
        (open) => {
            isCompanySizeOpen(open);
            if (open && locationsOpen) {
                locationFiltersRef?.current?.setFilter();
                isLocationsOpen(false);
            }
            if (open && companiesOpen) {
                companiesFilterRef?.current?.setFilter();
                isCompaniesOpen(false);
            }
        },
        [companiesOpen, locationsOpen]
    );

    useEffect(() => {
        const _isSelected = !!activeFilters?.remote?.length;
        if (remoteSelected !== _isSelected) {
            setRemoteSelected(_isSelected);
        }
    }, [activeFilters, remoteSelected]);

    const { isSM } = useViewport();

    useEffect(() => {
        if (activeFilters?.companies?.length === activeCompanies?.length) {
            return;
        }
        const companyMap = {};
        companyList?.forEach((i) => {
            companyMap[i.value] = i;
        });

        const _activeCompanies = activeFilters?.company_id
            ?.map((i) => {
                if (!companyMap[i]) {
                    return;
                }
                return {
                    label: companyMap[i].displayName,
                    value: companyMap[i].value,
                };
            })
            .filter((i) => !!i);

        if (!isEqual(_activeCompanies, activeCompanies)) {
            setActiveCompanies(_activeCompanies);
        }
    }, [activeFilters, activeCompanies, companyList]);

    useEffect(() => {
        if (
            activeFilters?.company_sizes?.length === activeCompanySizes?.length
        ) {
            return;
        }
        const companySizeMap = {};
        companySizes?.forEach((i) => {
            companySizeMap[i.value] = i;
        });

        const _activeCompanySizes = activeFilters?.company_size
            ?.map((i) => {
                if (!companySizeMap[i]) {
                    return;
                }
                return {
                    label: i,
                    value: i,
                };
            })
            .filter((i) => !!i);

        if (!isEqual(_activeCompanySizes, activeCompanySizes)) {
            setActiveCompanySizes(_activeCompanySizes);
        }
    }, [activeFilters, activeCompanySizes, companySizes]);

    useEffect(() => {
        if (activeFilters?.locations?.length === activeLocations?.length) {
            return;
        }
        const locationMap = {};
        locationList?.forEach((i) => {
            locationMap[i.value] = i;
        });

        const _activeLocations = activeFilters.locations
            ?.map((i) => {
                if (!locationMap[i]) {
                    return;
                }
                return {
                    label: locationMap[i].displayName,
                    value: locationMap[i].value,
                };
            })
            .filter((i) => !!i);
        if (!isEqual(_activeLocations, activeLocations)) {
            setActiveLocations(_activeLocations);
        }
    }, [activeFilters, activeLocations, locationList]);

    const onCompanySelect = useCallback(
        (value) => {
            // resetLocations.current = true;
            const type = 'company_id';
            setActiveCompanies(value);
            const filterArray = [];
            value?.forEach((v) => filterArray.push(v.value));
            const _activeCompanies = activeCompanies?.map((c) => c.value);
            if (!isEqual(filterArray.sort(), _activeCompanies.sort())) {
                setFilterByType({ type, values: filterArray });
                // find the corresponding company names for the selected copmany ids
                const companyNames = [];
                value?.forEach((v) => {
                    const company = companyList?.find(
                        (c) => c.value === v.value
                    );
                    if (company) {
                        companyNames.push(company.displayName);
                    }
                });
            }
        },
        [setFilterByType, activeCompanies]
    );

    const onCompanySizeSelect = useCallback(
        (value) => {
            // resetLocations.current = true;
            const type = 'company_size';
            setActiveCompanySizes(value);
            const filterArray = [];
            value?.forEach((v) => filterArray.push(v.value));
            const _activeCompanySizes = (activeCompanySizes || []).map(
                (c) => c.value
            );
            if (!isEqual(filterArray.sort(), _activeCompanySizes.sort())) {
                setFilterByType({ type, values: filterArray });
            }
        },
        [activeCompanySizes, setFilterByType]
    );

    const onLocationSelect = useCallback(
        (value) => {
            // resetCompanies.current = true;
            setActiveLocations(value);
            const type = 'locations';
            const filterArray = [];
            value?.forEach((v) => filterArray.push(v.value));
            const _activeLocations = activeLocations?.map((c) => c.value);
            if (!isEqual(filterArray.sort(), _activeLocations.sort())) {
                setFilterByType({ type, values: filterArray });
            }
        },
        [setFilterByType, activeLocations]
    );

    const onRemoteSelect = useCallback(() => {
        const type = 'remote';
        filterJobs({
            type,
            filter: { displayName: 'Remote', value: 1 },
        });
    }, [filterJobs]);

    const bodyRef = useRef(document.querySelector('body'));

    if (isSM) {
        return (
            <DesktopRender
                ref={{
                    companiesFilterRef,
                    companySizeFilterRef,
                    locationFiltersRef,
                }}
                bodyRef={bodyRef}
                remoteSelected={remoteSelected}
                onRemoteSelect={onRemoteSelect}
                locationsOpen={locationsOpen}
                isLocationsOpen={setLocationOpen}
                locations={locationList}
                onLocationSelect={onLocationSelect}
                hasRemote={hasRemote}
                activeLocations={activeLocations}
                onClearFilters={onClearFilters}
                setActiveCompanies={setActiveCompanies}
                activeCompanies={activeCompanies}
                setActiveCompanySizes={setActiveCompanySizes}
                activeCompanySizes={activeCompanySizes}
                isSM={isSM}
                setActiveLocations={setActiveLocations}
                isAuthenticatedUser={isAuthenticatedUser}
                companiesOpen={companiesOpen}
                companies={companyList}
                companySizes={companySizes}
                companySizeOpen={companySizeOpen}
                isCompaniesOpen={setCompaniesOpen}
                isCompanySizeOpen={setCompanySizeOpen}
                onCompanySelect={onCompanySelect}
                onCompanySizeSelect={onCompanySizeSelect}
            />
        );
    }

    return (
        <MobileFilter
            hasRemote={hasRemote}
            remoteSelected={remoteSelected}
            onRemoteSelect={onRemoteSelect}
            companies={companyList}
            companySizes={companySizes}
            locations={locationList}
            activeCompanies={activeCompanies}
            activeCompanySizes={activeCompanySizes}
            activeLocations={activeLocations}
            onClearFilters={onClearFilters}
            onLocationSelect={onLocationSelect}
            onCompanySelect={onCompanySelect}
            onCompanySizeSelect={onCompanySizeSelect}
            isAuthenticatedUser={isAuthenticatedUser}
        />
    );
};
